import { collection, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db, isUserAuthenticated } from './firebase';
import { ExchangeRate } from '../types/item';

// Collection reference
const EXCHANGE_RATES_COLLECTION = 'exchangeRates';

// Local cache to avoid redundant Firestore reads
const exchangeRateCache = new Map<string, number>();

/**
 * Converts a date to a Firestore-friendly timestamp range for the given day
 */
const getDateRangeForQuery = (date: Date): { startTime: Timestamp, endTime: Timestamp } => {
  // Create date at 00:00:00 of the given day
  const startDate = new Date(date);
  startDate.setHours(0, 0, 0, 0);
  
  // Create date at 23:59:59 of the given day
  const endDate = new Date(date);
  endDate.setHours(23, 59, 59, 999);
  
  return {
    startTime: Timestamp.fromDate(startDate),
    endTime: Timestamp.fromDate(endDate)
  };
};

/**
 * Creates a cache key for a date
 */
const getCacheKey = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

/**
 * Fetches the exchange rate for a specific date from Firestore
 * If no rate exists for exact date, returns the most recent rate before that date
 */
export const getExchangeRateForDate = async (date: Date): Promise<number> => {
  if (!db) {
    console.error('Firestore is not initialized');
    return 0;
  }

  // Check if user is authenticated
  if (!isUserAuthenticated()) {
    console.error('Error fetching exchange rate: User is not authenticated');
    return 0;
  }

  const cacheKey = getCacheKey(date);
  
  // Check cache first
  if (exchangeRateCache.has(cacheKey)) {
    return exchangeRateCache.get(cacheKey)!;
  }
  
  try {
    // Get timestamp range for the given date
    const { startTime, endTime } = getDateRangeForQuery(date);
    
    // Query for the exact date range
    const exactDateQuery = query(
      collection(db, EXCHANGE_RATES_COLLECTION),
      where('date', '>=', startTime),
      where('date', '<=', endTime)
    );
    
    const exactDateSnapshot = await getDocs(exactDateQuery);
    
    if (!exactDateSnapshot.empty) {
      const data = exactDateSnapshot.docs[0].data() as Partial<ExchangeRate>;
      const rate = data.usdToTry || 0;
      exchangeRateCache.set(cacheKey, rate);
      return rate;
    }
    
    // If no exact match, find the closest previous date
    const closestPreviousQuery = query(
      collection(db, EXCHANGE_RATES_COLLECTION),
      where('date', '<', startTime),
      orderBy('date', 'desc'),
      limit(1)
    );
    
    const closestPreviousSnapshot = await getDocs(closestPreviousQuery);
    
    if (!closestPreviousSnapshot.empty) {
      const data = closestPreviousSnapshot.docs[0].data() as Partial<ExchangeRate>;
      const rate = data.usdToTry || 0;
      exchangeRateCache.set(cacheKey, rate);
      return rate;
    }
    
    // If no previous rate found, get the earliest available rate
    const earliestRateQuery = query(
      collection(db, EXCHANGE_RATES_COLLECTION),
      orderBy('date', 'asc'),
      limit(1)
    );
    
    const earliestRateSnapshot = await getDocs(earliestRateQuery);
    
    if (!earliestRateSnapshot.empty) {
      const data = earliestRateSnapshot.docs[0].data() as Partial<ExchangeRate>;
      const rate = data.usdToTry || 0;
      exchangeRateCache.set(cacheKey, rate);
      return rate;
    }
  } catch (error) {
    console.error('Error fetching exchange rate:', error);
  }
  
  // Default fallback if no rates are found at all
  console.error('No exchange rates found in the database');
  return 0;
};

/**
 * Gets the most recent exchange rate from Firestore
 */
export const getCurrentExchangeRate = async (): Promise<number> => {
  try {
    // Check if user is authenticated
    if (!isUserAuthenticated()) {
      console.error('Error fetching current exchange rate: User is not authenticated');
      return 0;
    }
    
    const today = new Date();
    return getExchangeRateForDate(today);
  } catch (error) {
    console.error('Error fetching current exchange rate:', error);
    return 0;
  }
};

/**
 * Converts an amount from USD to TRY using a specific exchange rate
 */
export const convertUsdToTry = (amountUsd: number, exchangeRate: number): number => {
  return amountUsd * exchangeRate;
};

/**
 * Converts an amount from TRY to USD using a specific exchange rate
 */
export const convertTryToUsd = (amountTry: number, exchangeRate: number): number => {
  if (exchangeRate <= 0) return 0;
  return amountTry / exchangeRate;
};

/**
 * Formats a currency amount with the appropriate symbol and decimal places
 */
export const formatCurrency = (amount: number, currency: 'TRY' | 'USD'): string => {
  if (currency === 'TRY') {
    return `₺${amount.toFixed(2)}`;
  } else {
    return `$${amount.toFixed(2)}`;
  }
}; 